import React, { useState, useEffect } from 'react';
import { Clock, CheckCircle, XCircle, AlertCircle, Search, User } from 'lucide-react';
import api from '../api';

const AdminExpertConcerns = () => {
  const [concerns, setConcerns] = useState([]);
  const [experts, setExperts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedConcern, setSelectedConcern] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [updating, setUpdating] = useState(false);
  
  const [modalData, setModalData] = useState({
    status: '',
    selectedExpert: '',
    adminResponse: '',
    cancellationReason: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [concernsResponse, expertsResponse] = await Promise.all([
        api.get('/children/expert-requests/'),
        api.get('/children/child-experts/')
      ]);
      setConcerns(concernsResponse.data);
      setExperts(expertsResponse.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (concern) => {
    setSelectedConcern(concern);
    setModalData({
      status: concern.status,
      selectedExpert: concern.expert?.id || '',
      adminResponse: concern.admin_response || '',
      cancellationReason: ''
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedConcern(null);
    setModalData({
      status: '',
      selectedExpert: '',
      adminResponse: '',
      cancellationReason: ''
    });
  };

  const handleUpdateConcern = async () => {
    if (!selectedConcern) return;

    try {
      setUpdating(true);

      if (modalData.status === 'completed') {
        if (!modalData.selectedExpert || !modalData.adminResponse.trim()) {
          alert('Please select an expert and provide a response');
          setUpdating(false);
          return;
        }
        await api.post(`/children/expert-requests/${selectedConcern.id}/assign-expert/`, {
          expert_id: modalData.selectedExpert
        });
        await api.post(`/children/expert-requests/${selectedConcern.id}/add-response/`, {
          admin_response: modalData.adminResponse
        });
        await api.post(`/children/expert-requests/${selectedConcern.id}/update-status/`, {
          status: 'completed'
        });
      } else if (modalData.status === 'cancelled') {
        if (!modalData.cancellationReason.trim()) {
          alert('Please provide a cancellation reason');
          setUpdating(false);
          return;
        }
        await api.post(`/children/expert-requests/${selectedConcern.id}/update-status/`, {
          status: 'cancelled'
        });
        await api.post(`/children/expert-requests/${selectedConcern.id}/add-response/`, {
          admin_response: modalData.cancellationReason
        });
      } else {
        await api.post(`/children/expert-requests/${selectedConcern.id}/update-status/`, {
          status: modalData.status
        });
      }

      await fetchData();
      handleCloseModal();
    } catch (err) {
      alert('Failed to update concern: ' + err.message);
    } finally {
      setUpdating(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-5 h-5" />;
      case 'in_progress':
        return <AlertCircle className="w-5 h-5" />;
      case 'completed':
        return <CheckCircle className="w-5 h-5" />;
      case 'cancelled':
        return <XCircle className="w-5 h-5" />;
      default:
        return <AlertCircle className="w-5 h-5" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'Pending';
      case 'in_progress':
        return 'In Progress';
      case 'completed':
        return 'Completed';
      case 'cancelled':
        return 'Cancelled';
      default:
        return 'Unknown';
    }
  };

  const filteredConcerns = concerns.filter(concern => {
    const matchesSearch = 
      concern.child_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      concern.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'ALL' || concern.status === filterStatus.toLowerCase();
    return matchesSearch && matchesFilter;
  });

  const pendingCount = concerns.filter(c => c.status === 'pending').length;
  const inProgressCount = concerns.filter(c => c.status === 'in_progress').length;
  const completedCount = concerns.filter(c => c.status === 'completed').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-[#1e3a5f]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="bg-red-50 text-red-600 p-6 rounded-xl shadow-lg">
          <p className="font-semibold">Error: {error}</p>
        </div>
      </div>
    );
  }

  const StatCard = ({ icon: Icon, title, value, color, bgColor }) => (
    <div className={`${bgColor} rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <p className="text-3xl font-bold text-[#1e3a5f]">{value}</p>
        </div>
        <div className={`${color} p-4 rounded-xl`}>
          <Icon className="w-8 h-8 text-white" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-4xl font-bold text-[#1e3a5f] mb-2">Expert Concerns Management</h1>
          <p className="text-gray-600">Review and respond to parent concerns</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          icon={Clock}
          title="Pending"
          value={pendingCount}
          color="bg-yellow-500"
          bgColor="bg-yellow-50"
        />
        <StatCard
          icon={AlertCircle}
          title="In Progress"
          value={inProgressCount}
          color="bg-blue-500"
          bgColor="bg-blue-50"
        />
        <StatCard
          icon={CheckCircle}
          title="Completed"
          value={completedCount}
          color="bg-green-500"
          bgColor="bg-green-50"
        />
      </div>

      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by child name or concern description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1e3a5f] focus:border-transparent"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1e3a5f] focus:border-transparent"
          >
            <option value="ALL">All Status</option>
            <option value="PENDING">Pending</option>
            <option value="IN_PROGRESS">In Progress</option>
            <option value="COMPLETED">Completed</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-[#1e3a5f] to-[#2a4b73] p-6">
          <h2 className="text-2xl font-bold text-white">All Concerns</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Child</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Concern</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Expert</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Date</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredConcerns.map((concern) => (
                <tr
                  key={concern.id}
                  onClick={() => handleOpenModal(concern)}
                  className="cursor-pointer hover:bg-blue-50 transition-colors duration-150"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#FDB913] to-[#FFA500] flex items-center justify-center text-white font-bold">
                        {concern.child_name?.charAt(0).toUpperCase() || 'C'}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{concern.child_name || 'Unknown'}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 max-w-md truncate">
                      {concern.description}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {concern.expert?.name || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {new Date(concern.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 inline-flex items-center gap-2 text-xs leading-5 font-semibold rounded-full ${getStatusColor(concern.status)}`}>
                      {getStatusIcon(concern.status)}
                      {getStatusText(concern.status)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && selectedConcern && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={handleCloseModal}
        >
          <div
            className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-gradient-to-r from-[#1e3a5f] to-[#2a4b73] px-6 py-5 rounded-t-3xl flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Manage Concern</h2>
              <button
                onClick={handleCloseModal}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
              >
                <XCircle className="w-6 h-6 text-white" />
              </button>
            </div>

            <div className="p-6 md:p-8">
              <div className="mb-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#FDB913] to-[#FFA500] flex items-center justify-center text-white font-bold text-lg">
                    {selectedConcern.child_name?.charAt(0).toUpperCase() || 'C'}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{selectedConcern.child_name}</h3>
                    <p className="text-sm text-gray-500">
                      {new Date(selectedConcern.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Concern Description:</h4>
                <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
                  <p className="text-gray-700 leading-relaxed">{selectedConcern.description}</p>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Update Status *
                </label>
                <select
                  value={modalData.status}
                  onChange={(e) => setModalData({ ...modalData, status: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1e3a5f] focus:border-transparent"
                >
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              {modalData.status === 'completed' && (
                <>
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Select Expert *
                    </label>
                    <select
                      value={modalData.selectedExpert}
                      onChange={(e) => setModalData({ ...modalData, selectedExpert: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1e3a5f] focus:border-transparent"
                    >
                      <option value="">Select an expert...</option>
                      {experts.map((expert) => (
                        <option key={expert.id} value={expert.id}>
                          {expert.name} - {expert.speciality}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Expert Response *
                    </label>
                    <textarea
                      value={modalData.adminResponse}
                      onChange={(e) => setModalData({ ...modalData, adminResponse: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1e3a5f] focus:border-transparent resize-none"
                      rows="5"
                      placeholder="Provide expert guidance and recommendations..."
                    />
                  </div>

                  {modalData.selectedExpert && (
                    <div className="mb-6 bg-blue-50 rounded-2xl p-4 border border-blue-100">
                      <div className="flex items-start gap-3">
                        <User className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div className="flex-1">
                          <p className="text-sm font-semibold text-blue-800 mb-1">Selected Expert</p>
                          {experts.find(e => e.id === modalData.selectedExpert) && (
                            <div className="text-sm text-blue-700">
                              <p className="font-medium">{experts.find(e => e.id === modalData.selectedExpert).name}</p>
                              <p>{experts.find(e => e.id === modalData.selectedExpert).speciality}</p>
                              <p className="text-xs mt-1">{experts.find(e => e.id === modalData.selectedExpert).contact}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {modalData.status === 'cancelled' && (
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Cancellation Reason *
                  </label>
                  <textarea
                    value={modalData.cancellationReason}
                    onChange={(e) => setModalData({ ...modalData, cancellationReason: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1e3a5f] focus:border-transparent resize-none"
                    rows="4"
                    placeholder="Please provide a reason for cancelling this concern..."
                  />
                </div>
              )}

              {selectedConcern.admin_response && (
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Current Response:</h4>
                  <div className="bg-green-50 rounded-2xl p-4 border border-green-100">
                    <p className="text-gray-700 leading-relaxed">{selectedConcern.admin_response}</p>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300 transition-all"
                  disabled={updating}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateConcern}
                  className="flex-1 py-3 bg-gradient-to-r from-[#1e3a5f] to-[#2a4b73] text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={updating}
                >
                  {updating ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Updating...
                    </div>
                  ) : (
                    'Update Concern'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminExpertConcerns;
